/**
 * Vulnerability Tag Modal Component
 *
 * Modal for adding tags to vulnerabilities with optimistic updates and
 * duplicate prevention.
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Duplicate tag prevention (case-insensitive)
 * - Granular error reporting per vulnerability
 * - Automatic cache invalidation and refresh
 *
 * Improvements over asset implementation:
 * - Uses optimistic updates (fixes anti-pattern #3)
 * - Prevents duplicates (fixes anti-pattern #5)
 * - Better error handling with detailed feedback
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { queryClient } from '@/queryclient';
import { AddTag } from '@/sections/AddTag';
import { useMutation } from '@/utils/api';
import {
  tagAddedError,
  tagAddedSuccess,
  tagValidationError,
} from '@/utils/tagToastMessages';
import { validateTagInput } from '@/utils/tagValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  riskKeys: string[];
  onSuccess?: () => void;
  /**
   * Bulk selection state from caller. When provided AND shouldUseBulk returns true,
   * the modal delegates to onBulkRequested instead of per-item PUTs.
   *
   * INVARIANT: riskKeys MUST be derived from the SAME source as selectionState.
   * Callers either:
   *   (a) pass neither selectionState nor onBulkRequested (per-item modal), OR
   *   (b) pass selectionState; riskKeys should be selectionState.keys or equivalent.
   */
  selectionState?: BulkSelectionState;
  /** Called when bulk path is triggered; caller handles BulkConfirmationDialog. */
  onBulkRequested?: (tag: string) => void;
}

interface TagOperationResult {
  succeeded: number;
  failed: number;
  duplicates: number;
  results: Array<{ key: string; skipped?: boolean; reason?: string }>;
}

export const VulnerabilityTag = ({
  open,
  onClose,
  riskKeys,
  onSuccess,
  selectionState,
  onBulkRequested,
}: Props) => {
  const axios = useAxios();

  const { mutate: updateRisks, status: updatingRisks } = useMutation<
    TagOperationResult,
    Error,
    string
  >({
    defaultErrorMessage: 'Failed to add tag',
    mutationFn: async (newTag: string) => {
      // Fetch current state for all risks
      const promises = riskKeys.map(async riskKey => {
        try {
          const currentRisk = await axios.get(`/my`, {
            params: {
              label: 'risk',
              key: riskKey,
              exact: true,
            },
          });

          const risk = currentRisk.data.risks?.[0];
          if (!risk) {
            throw new Error(`Risk not found: ${riskKey}`);
          }

          const existingTags = risk.tags || [];

          // Check for duplicate (case-insensitive)
          const isDuplicate = existingTags.some(
            (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
          );

          if (isDuplicate) {
            return {
              key: riskKey,
              skipped: true,
              reason: 'Tag already exists',
            };
          }

          const payload = {
            key: riskKey,
            name: risk.name,
            status: risk.status,
            comment: risk.comment || '',
            tags: [...existingTags, newTag],
          };

          await axios.put('/risk', payload);

          return {
            key: riskKey,
            skipped: false,
          };
        } catch (error) {
          console.error(`[VulnerabilityTag] Failed for ${riskKey}:`, error);
          throw error;
        }
      });

      const results = await Promise.allSettled(promises);

      // Process results
      const succeeded = results.filter(
        r => r.status === 'fulfilled' && !r.value.skipped
      ).length;

      const duplicates = results.filter(
        r => r.status === 'fulfilled' && r.value.skipped
      ).length;

      const failed = results.filter(r => r.status === 'rejected').length;

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            key: riskKeys[i],
            error: r.reason?.message || 'Unknown error',
          }));
        console.error('[VulnerabilityTag] Failed operations:', failedResults);
      }

      return {
        succeeded,
        duplicates,
        failed,
        results: results.map((r, i) => {
          if (r.status === 'fulfilled') {
            return r.value;
          }
          return { key: riskKeys[i], skipped: false };
        }),
      };
    },
    onMutate: async newTag => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueriesData({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Optimistically update all risk queries
      queryClient.setQueriesData(
        {
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'risk';
          },
        },
        (old: unknown) => {
          const data = old as
            | { risks?: Array<{ key: string; tags?: string[] }> }
            | undefined;
          if (!data?.risks) return old;

          return {
            ...data,
            risks: data.risks.map(risk => {
              if (riskKeys.includes(risk.key)) {
                const existingTags = risk.tags || [];
                // Only add if not duplicate (case-insensitive)
                const isDuplicate = existingTags.some(
                  (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
                );
                if (isDuplicate) return risk;

                return {
                  ...risk,
                  tags: [...existingTags, newTag],
                };
              }
              return risk;
            }),
          };
        }
      );

      return { previousData };
    },
    onError: (_err: Error, _newTag: string, context: unknown) => {
      const contextData = context as
        | { previousData?: Array<[unknown, unknown]> }
        | undefined;
      // Rollback on error
      if (contextData?.previousData) {
        contextData.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey as string[], data);
        });
      }
      toast.error('Failed to add tag. Changes have been reverted.');
    },
    onSettled: async () => {
      // Invalidate queries after mutation completes
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
        refetchType: 'all',
      });
    },
  });

  const handleSubmit = async (tag: string) => {
    // Validate input
    const validation = validateTagInput(tag);
    if (!validation.isValid) {
      toast.error(tagValidationError(validation.error!));
      return;
    }

    // Dev-mode divergence guard (mirrors RemoveTagModal.tsx:194-206, R4 regression prevention).
    if (
      import.meta.env.DEV &&
      selectionState &&
      selectionState.mode === 'manual' &&
      riskKeys.length > 0 &&
      selectionState.keys.length !== riskKeys.length
    ) {
      console.warn(
        '[VulnerabilityTag] selectionState.keys.length diverges from riskKeys.length — ' +
          'likely a caller bug. Bulk path will operate on selectionState.'
      );
    }

    // Bulk path: delegate to caller's BulkConfirmationDialog.
    // MUST be BEFORE the per-item mutation block below to prevent double-apply.
    if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
      onBulkRequested(validation.sanitized!);
      onClose();
      return;
    }

    try {
      const result = await new Promise<TagOperationResult>(
        (resolve, reject) => {
          updateRisks(validation.sanitized!, {
            onSuccess: resolve,
            onError: reject,
          });
        }
      );

      // Show appropriate feedback based on results
      if (
        result.succeeded > 0 &&
        result.failed === 0 &&
        result.duplicates === 0
      ) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagAddedSuccess('vulnerability', count));
        onSuccess?.();
        onClose();
      } else if (
        result.succeeded > 0 &&
        (result.failed > 0 || result.duplicates > 0)
      ) {
        let message = `Tag added to ${result.succeeded} ${result.succeeded > 1 ? 'vulnerabilities' : 'vulnerability'}`;
        if (result.duplicates > 0) {
          message += `, skipped ${result.duplicates} (already tagged)`;
        }
        if (result.failed > 0) {
          message += `, failed ${result.failed}`;
        }
        toast.warning(message);
        onSuccess?.();
        onClose();
      } else if (result.duplicates === riskKeys.length) {
        toast.warning('All selected vulnerabilities already have this tag');
      } else {
        toast.error(tagAddedError('vulnerability'));
      }
    } catch (error) {
      console.error('[VulnerabilityTag] Mutation failed:', error);
      // Error toast already shown in onError
    }
  };

  return (
    <AddTag
      open={open}
      onClose={onClose}
      disabled={updatingRisks === 'pending'}
      description="Tags help organize and classify vulnerabilities in your attack surface."
      onSubmit={handleSubmit}
      entity="risk"
    />
  );
};
